import { makeAutoObservable } from 'mobx'

import type { components, operations } from '../constants/schema'
import type {
  CommunityList,
  HistoryList,
  NoticeList,
  QnaDetailList,
  QnaList,
  ResNoticeDetail,
  ResProductDetail,
  ResultRegisterQna,
  Terms,
  UserInfo,
  WithdrawalAccount,
} from '../constants/types'

import { HttpReq, updateToken } from '../utils'

import RootStore from './root'
import { PagingSubStore, SingleSubStore } from './subStore'

import type { BankName } from '../components/bankIcon/BankIcon'
import type { SecurityName } from '../components/securityIcon/SecurityIcon'

export class MyStore {
  rootStore: RootStore
  withdrawalBankList: { value: string[]; title: string }[] = []
  withdrawalAccountInfo: object | undefined = undefined

  /* 메인 자산 summary */
  // assetSummaryStore = new SingleSubStore<ResAssetSummary>(`/purchase/summary`)
  assetSummaryStore = new SingleSubStore<components['schemas']['ResUserAssetSummary']>(`/user/current/assetSummary`)

  /* 총 보유 자산 */
  // assetHoldPagingStore = new PagingSubStore<ProductList>('/purchase/products') // 소유중인 리스트
  assetHoldPagingStore = new PagingSubStore<components['schemas']['UserProductDto']>('/user/current/product/holding') // 소유중인 리스트
  // assetFinishPagingStore = new PagingSubStore<ProductList>(`/purchase/products?isFinished=true`) //매각 완료 리스트
  assetFinishPagingStore = new PagingSubStore<components['schemas']['UserProductDto']>(`/user/current/product/sold`) //매각 완료 리스트
  assetHistoryPagingStore = new PagingSubStore<HistoryList>('/purchase') //  거래내역 리스트

  /* 작품 상세 */
  // 거래내역
  purchasePagingStore = new SingleSubStore<components['schemas']['PurchaseLogDto'][]>(
    (param?: operations['UserProductController_getUserProductPurchases']['parameters']['path']) =>
      `/user/product/${param?.userProductId}/purchases`,
  )
  // 작품 정보
  purchaseDetailStore = new SingleSubStore<components['schemas']['UserProductDto']>(
    (param?: operations['UserProductController_getUserProduct']['parameters']['path']) =>
      `/user/product/${param?.userProductId}`,
  )
  productDetailStore = new SingleSubStore<ResProductDetail>( // 작품 상세 정보
    (param?: Record<string, any>) => `/product/${param?.productId}`,
  )
  productDisposalStore = new SingleSubStore<components['schemas']['UserProductSettleInformationDto']>( // 작품 매각 정보
    (param: { userProductId: number }) => `/user/product/${param.userProductId}/info/settle`,
  )
  communityPagingStore = new PagingSubStore<CommunityList>( // 작품 커뮤니티
    (param?: Record<string, any>) => `/product/${param?.productId}/post`,
  )

  /* 예치금 */
  accountPagingStore = new PagingSubStore<components['schemas']['CashTransactionLogDto']>(
    `/user/current/cash/transaction`,
  ) // 입출금 내역
  depositAccountStore = new SingleSubStore<components['schemas']['DepositAccountDto']>(`/user/current/depositAccount`) // 충전 계좌 조회
  unmaskDepositAccountStore = new SingleSubStore<components['schemas']['UnmaskedDepositAccountDto']>(
    `/user/current/depositAccount/unmask`,
  ) // 마스킹 안 된 충전 계좌 조회
  withdrawalAccountStore = new SingleSubStore<components['schemas']['MaskedWithdrawalAccountDto']>(
    `/user/current/withdrawalAccount`,
  ) // 출금 계좌 조회
  unmaskWithdrawalAccountStore = new SingleSubStore<components['schemas']['UnmaskedWithdrawalAccountDto']>(
    '/user/current/withdrawalAccount/unmask',
  ) // 마스킹 안 된 출금 계좌 조회

  /* 공지사항 */
  noticePagingStore = new PagingSubStore<NoticeList>(`/notice`) // 공지사항 리스트
  noticeDetailStore = new SingleSubStore<ResNoticeDetail>( // 상세 정보
    (param?: Record<string, any>) => `/notice/${param?.notificationId}`,
  )

  /* 문의사항 */
  qnaPagingStore = new PagingSubStore<QnaList>(`/user/qna`) // 문의사항 리스트
  qnaDetail = new SingleSubStore<components['schemas']['QnaDto']>(
    (param?: Record<string, any>) => `/user/qna/${param?.qnaId}`,
  )
  qnaDetailPagingStore = new PagingSubStore<QnaDetailList>( // 문의사항 상세 페이지
    (param?: Record<string, any>) => `/user/qna/${param?.qnaId}/chats`,
  )

  /* 마케팅 수신 동의 */
  // marketingPushStore = new SingleSubStore<components['schemas']['ResUserTermsMarketing']>(
  //   '/user/current/terms/marketing',
  // ) // 마케팅 수신 동의 조회
  // 로그인유저 페이지별 선택약관 전체동의 여무 확인
  termsApprovedStore = new SingleSubStore<components['schemas']['ResBooleanResult']>(
    (param: operations['UserController_getCurrentUserTermsApprovedByPageName']['parameters']['path']) =>
      `/user/current/terms/page/${param.pageName}/approved`,
  )

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  cancelProduct = async (id: number): Promise<unknown> => {
    return await HttpReq<unknown>({
      method: 'POST',
      url: `/product/${id}/cancel`,
    })
  }

  /* 예치금 출금 */
  withdrawalBalance = async (data: components['schemas']['ReqUserDepositAccountWithdrawalDto']): Promise<never> => {
    return await HttpReq({
      method: 'POST',
      url: `/user/current/depositAccount/withdrawal`,
      data,
      isLoadable: false,
    })
  }

  /* 문의사항 생성 */
  registerQna = async (data: { title: string; content: string }) => {
    await HttpReq<ResultRegisterQna>({
      method: 'POST',
      url: `/user/qna`,
      data: data,
    })
  }

  /* 문의사항 추가 문의 */
  registerQnaChats = async (qnaId: number, content: string) => {
    await HttpReq<ResultRegisterQna>({
      method: 'POST',
      url: `/user/qna/${qnaId}/chats`,
      data: { content: content },
    })
  }

  /* 유저 정보 수정 */
  modifyUserInfo = async (data?: UserInfo) => {
    const updateUserInfo = await HttpReq<components['schemas']['UserDto']>({
      method: 'PUT',
      url: `/user/current`,
      data,
    })
    if (updateUserInfo) {
      this.rootStore.authStore.getUserInfo()
    }
  }

  /* 정산하기 */
  settlement = async (userProductId: number) => {
    return await HttpReq<components['schemas']['UserProductDto']>({
      method: 'POST',
      url: `/user/product/${userProductId}/settle`,
    })
  }

  setWithdrawalAccountInfo = (data: WithdrawalAccount) => {
    this.withdrawalAccountInfo = data
  }

  /* 출금 계좌 등록 가능한 은행 목록 조회*/
  getWithdrawalBankList = async () => {
    // 출금 가능한 backList api 호출
    const withdrawalBackList = await HttpReq<{
      bank: { code: string; name: BankName }[]
      securities: { code: string; name: SecurityName }[]
    }>({
      method: 'GET',
      url: `/nh/bankList`,
    })

    return withdrawalBackList
  }

  /* 입금 계좌 생성 */
  createDepositAccount = async (data: Terms[]) => {
    return await HttpReq<Record<string, string>>({
      method: 'POST',
      url: `/user/current/depositAccount`,
      data: { terms: data },
    })
  }

  /* 출금 계좌 생성 */
  createWithdrawalAccount = async (data?: components['schemas']['WithdrawalAccountUpdateDto']) => {
    try {
      return await HttpReq<components['schemas']['WithdrawalAccountDto']>({
        method: 'POST',
        url: `/user/current/withdrawalAccount`,
        data: data ? data : this.withdrawalAccountInfo,
      })
    } catch (error) {
      return error
    }
  }

  /* 출금 계좌 변경 */
  updateWithdrawalAccount = async (data?: components['schemas']['WithdrawalAccountUpdateDto']) => {
    try {
      return await HttpReq<components['schemas']['WithdrawalAccountDto']>({
        method: 'PUT',
        url: '/user/current/withdrawalAccount',
        data,
      })
    } catch (error) {
      return error
    }
  }

  withdrawalUser = async () => {
    await HttpReq<Record<string, string>>({
      method: 'DELETE',
      url: `/user/current`,
    })
    updateToken('remove')
  }

  // toggleMarketingPush = async () => {
  //   return await HttpReq<components['schemas']['ResUserTermsMarketing']>({
  //     method: 'POST',
  //     url: '/user/current/terms/marketing',
  //   })
  // }

  toggleTerms = async (data: operations['UserController_toggleCurrentUserTermsByPageName']['parameters']['path']) => {
    return await HttpReq<components['schemas']['ResBooleanResult']>({
      method: 'POST',
      url: `/user/current/terms/page/${data.pageName}/toggle`,
    })
  }

  // 판매율
  get saleRate() {
    if (this.productDetailStore.content) {
      const detailContent = this.productDetailStore.content
      const saleRate =
        ((detailContent?.tokenCount - detailContent.activeProductSale?.remainCount) / detailContent?.tokenCount) * 100
      return saleRate
    } else {
      return 0
    }
  }

  // 투표율
  get voteRateDetail() {
    if (this.productDetailStore.content) {
      const detailContent = this.productDetailStore.content
      const voteRate = (detailContent?.voteCount / detailContent.activeProductVote?.suppliedTokenCount) * 100
      return voteRate
    } else {
      return 0
    }
  }

  // 최초 작품 금액
  get initSalePriceDetail() {
    if (this.productDetailStore.content) {
      const detailContent = this.productDetailStore.content
      const initSalePrice = detailContent.activeProductSale?.cost * detailContent?.tokenCount
      return initSalePrice
    } else {
      return 0
    }
  }

  // 매각 수익율
  get priceRate() {
    if (this.productDetailStore.content && this.initSalePriceDetail) {
      const detailContent = this.productDetailStore.content
      const priceRate =
        ((detailContent.sellHistory?.salePrice - this.initSalePriceDetail) / this.initSalePriceDetail) * 100
      return priceRate
    } else {
      return 0
    }
  }

  // 변동 자산률 - 보유 자산 - 작품 상세 페이지
  get assetChangeRate() {
    if (this.productDetailStore.content) {
      const averageCost = Number(this.productDetailStore.content?.averageCost)
      const assetChangeRate = ((this.productDetailStore.content?.currentCost - averageCost) / averageCost) * 100
      return assetChangeRate
    } else {
      return 0
    }
  }

  // 변동 자산률 - 메인 summary
  get balanceChangeRate() {
    if (this.assetSummaryStore.content) {
      const withdrawableBalance = Number(this.assetSummaryStore.content?.withdrawableBalance)
      const totalBalance = Number(this.assetSummaryStore.content?.totalBalance)
      const balanceChangeRate = ((totalBalance - withdrawableBalance) / withdrawableBalance) * 100
      return balanceChangeRate
    } else {
      return 0
    }
  }

  // 사용 가능 금액
  get tradableBalance() {
    if (this.assetSummaryStore.content) {
      const depositBalance = Number(this.assetSummaryStore.content?.depositBalance) // 예치금
      // const tradeableBalance = Number(this.assetSummaryStore.content?.tradeableBalance) // 마켓 대기 금액
      // const tradableBalance = depositBalance - tradeableBalance
      const tradableBalance = depositBalance
      return tradableBalance
    } else {
      return 0
    }
  }

  // 예상 수익금
  get expectedCost() {
    if (this.productDisposalStore.content) {
      const detailData = this.productDisposalStore.content
      const expectedCost = detailData?.sellCost * detailData?.count
      return expectedCost
    } else {
      return 0
    }
  }
}
